import * as React from "react";
import Form, {
    BooleanField,
    DateRangeField,
    FileField,
    SelectField,
} from "../../components/Form";
import QueryDataTable from "../../components/QueryDataTable";
import { FileUpload } from "@mui/icons-material";
import { Dialog, IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import Authentication from "../../modules/authentication";
import { dateFilterColumn } from "../../components/DataTable";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

const gApiClient = Authentication.getAPIClient();

export default function APIReports() {
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const reportClients = useSelector((state) => state.reports.reportClients);
    const clientsById = useSelector((state) => state.reports.reportClientsById);
    const defaultClient = reportClients.filter((x) => x.is_default)[0];
    const yesterday = DateTime.now()
        .startOf("day")
        .minus({ days: 1 })
        .toMillis();

    const QUERY_FIELDS = [
        {
            name: "report_type",
            label: "Report Type",
            type: SelectField,
            optionsFromSelector: (state) =>
                state.reports.reportTypes.map((s) => ({
                    id: s,
                    label: s,
                })),
            required: true,
        },
        {
            name: "client_id",
            label: "Client",
            type: SelectField,
            optionsFromSelector: (state) =>
                reportClients.map((x) => ({
                    id: x._id,
                    label: x.name,
                })),
            required: false,
            defaultValue: defaultClient?._id,
        },
        {
            name: "date_range",
            label: "Date Range",
            type: DateRangeField,
            defaultValue: [yesterday, yesterday],
        },
    ];

    const onSubmit = async (values) => {
        const isXLS =
            values.report_file.filename.endsWith(".xls") ||
            values.report_file.filename.endsWith(".xlsx");

        const response = await gApiClient.callApi(
            `upload_report/${values.report_type}?overwrite=${
                values.overwrite ? "1" : "0"
            }&file_format=${isXLS ? "xls" : "csv"}`,
            {
                method: "POST",
                body: values.report_file.fileBody,
            },
        );

        console.log("Response", response.data);

        setShowUploadDialog(false);
    };

    const COLUMNS = [
        dateFilterColumn("available_from", "Available From", {
            dateFormat: "dd/MM/yyyy HH:mm",
        }),
    ];

    const UPLOAD_REPORT_FIELDS = [
        {
            name: "report_type",
            label: "Report Type",
            type: SelectField,
            optionsFromSelector: (state) =>
                state.reports.reportTypes.map((s) => ({
                    id: s,
                    label: s,
                })),
            required: true,
        },
        {
            name: "report_file",
            label: "Report File",
            type: FileField,
            required: true,
        },
        {
            name: "overwrite",
            label: "Overwrite Previous Report?",
            type: BooleanField,
            required: true,
            defaultValue: true,
        },
    ];

    // Format the CSV filename
    const formatCSVFilename = (values) => {
        return `report-${values.report_type}.csv`;
    };

    const validateValues = (values) => {
        if (!values.client_id) return;

        const chosenClient = clientsById[values.client_id];
        if (!chosenClient.permissions[values.report_type]) {
            throw new Error(
                `Client ${chosenClient.name} does not have permissions for ${values.report_type} report`,
            );
        }
    };

    return (
        <>
            <Dialog
                open={showUploadDialog}
                onClose={() => {
                    setShowUploadDialog(false);
                }}
                maxWidth={"md"}
                fullWidth={true}
            >
                <div className="p-5 flex flex-col items-center">
                    <div className="text-center mb-8 text-3xl">
                        Upload Report
                    </div>
                    <Form
                        fields={UPLOAD_REPORT_FIELDS}
                        onSubmit={onSubmit}
                        submitButtonText="Upload"
                        hideResetButton={true}
                        additionalButtons={[
                            {
                                text: "Cancel",
                                onPress: () => setShowUploadDialog(false),
                            },
                        ]}
                    />
                </div>
            </Dialog>
            <QueryDataTable
                actions={
                    <Tooltip title={"Upload Report"}>
                        <IconButton onClick={() => setShowUploadDialog(true)}>
                            <FileUpload />
                        </IconButton>
                    </Tooltip>
                }
                tableColumns={COLUMNS}
                onQuerySet={validateValues}
                showAllColumns={true}
                queryFields={QUERY_FIELDS}
                queryAPIPath="report"
                dynamicLoad={true}
                formatCSVFilename={formatCSVFilename}
            />
        </>
    );
}
